import React, { FC, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, type MenuProps } from '@mui/material';
import { AxiosError } from 'axios';
import { useToggle } from 'usehooks-ts';

import {
  ProcessResponseDTOAccessModesItem,
  ProcessResponseDTOStatus,
  ProcessTalentInvitationRequestAccessMode,
  ProcessTalentInvitationRequestSendMode,
  TalentResponseDTOStatus,
  TestSessionSummaryDTO,
  useInviteTalentToProcess,
  useRemoveTalentsFromProcess
} from '@/api/generated';
import FolderMinusSign from '@/assets/icons/folder-minus.svg?react';
import PaperPlane from '@/assets/icons/paper-plane.svg?react';
import RectangleAsterisks from '@/assets/icons/rectangle-asterisks.svg?react';
import ConfirmationModal from '@/components/ConfirmationModal/ConfirmationModal';
import DropdownMenu from '@/components/DropdownMenu/DropdownMenu';
import DropdownMenuItem from '@/components/DropdownMenu/DropdownMenuItem';
import GeneratePasswordModal from '@/components/GeneratePasswordModal';
import { useProcessPermissions } from '@/hooks/permissions/useProcessPermissions';
import { useSnackbar } from '@/hooks/useSnackbar';
import { isComplete, isInProgress, isRefused, isTalentFinished } from '@/utils/testSessions';
import { getUserFullName } from '@/utils/user';

/**
 * Common process data interface that works with both ProcessResponseDTO and ProcessTalentResponseDTO
 */
interface TalentProcessActionsMenuProcessData {
  id?: string;
  name?: string;
  status?: string;
  accessModes?: string[];
  testSessionSummaries?: TestSessionSummaryDTO[];
}

/**
 * Common talent data interface
 */
interface TalentProcessActionsMenuTalentData {
  id?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  status?: TalentResponseDTOStatus;
}

interface TalentProcessActionsMenuProps {
  process: TalentProcessActionsMenuProcessData;
  talent: TalentProcessActionsMenuTalentData;
  /** Called after successful removal from process */
  onRemoveSuccess?: () => void;
  /** Menu positioning props */
  menuProps?: Pick<MenuProps, 'anchorOrigin' | 'transformOrigin'>;
  /** Whether to auto-close the menu after clicking an item */
  autoClose?: boolean;
}

/**
 * Check if the process is in a "busy" state where removal should be disabled
 * Works with both ProcessResponseDTO and ProcessTalentResponseDTO
 */
const isProcessBusy = (process: TalentProcessActionsMenuProcessData): boolean => {
  const testSessionSummaries = process.testSessionSummaries ?? [];

  return (
    process.status === ProcessResponseDTOStatus.CANCELLED ||
    process.status === ProcessResponseDTOStatus.COMPLETED ||
    testSessionSummaries.some(session => isInProgress(session) || isRefused(session) || isComplete(session))
  );
};

/**
 * Unified actions menu for talent-process operations.
 * Used in both TalentCard (process screen) and Title (talent screen).
 * Provides consistent actions, permissions, and business logic across both contexts.
 */
const TalentProcessActionsMenu: FC<TalentProcessActionsMenuProps> = ({
  process,
  talent,
  onRemoveSuccess,
  menuProps,
  autoClose
}) => {
  const { t } = useTranslation();
  const { showMessage } = useSnackbar();

  const { value: { canRemoveTalents, canSendInvitation } = {} } = useProcessPermissions({
    variables: { processId: process.id! }
  });

  const [showConfirmRemoveFromProcess, setShowConfirmRemoveFromProcess] = useState(false);
  const { mutate: removeTalentFromProcess, isLoading: isRemoving } = useRemoveTalentsFromProcess<AxiosError>();

  const [isPasswordModalOpen, togglePasswordModalOpen] = useToggle(false);
  const {
    mutateAsync: inviteTalentToProcess,
    isLoading: isGenerating,
    data: invitationResponse
  } = useInviteTalentToProcess();

  const isRemoveDisabled = useMemo(
    () =>
      isRemoving ||
      isProcessBusy(process) ||
      talent.status === TalentResponseDTOStatus.IN_TEST ||
      talent.status === TalentResponseDTOStatus.COMPLETED,
    [isRemoving, process, talent.status]
  );

  const canGeneratePassword = useMemo(
    () =>
      canSendInvitation &&
      process.accessModes?.includes(ProcessResponseDTOAccessModesItem.PASSWORD) &&
      !isTalentFinished(process.testSessionSummaries),
    [canSendInvitation, process.accessModes, process.testSessionSummaries]
  );

  const isGeneratePasswordDisabled = useMemo(
    () =>
      process.status === ProcessResponseDTOStatus.UNPUBLISHED ||
      process.status === ProcessResponseDTOStatus.ON_HOLD ||
      process.status === ProcessResponseDTOStatus.CANCELLED ||
      process.status === ProcessResponseDTOStatus.COMPLETED ||
      process.testSessionSummaries?.some(session => isComplete(session) || isRefused(session)),
    [process.status, process.testSessionSummaries]
  );

  const canSendInvitationByEmail = useMemo(
    () =>
      canSendInvitation &&
      process.accessModes?.includes(ProcessResponseDTOAccessModesItem.UNIQUE_LINK) &&
      !isTalentFinished(process.testSessionSummaries),
    [canSendInvitation, process.accessModes, process.testSessionSummaries]
  );

  const isSendInvitationDisabled = useMemo(
    () =>
      !talent.email ||
      process.status === ProcessResponseDTOStatus.UNPUBLISHED ||
      process.status === ProcessResponseDTOStatus.ON_HOLD ||
      process.status === ProcessResponseDTOStatus.CANCELLED ||
      process.status === ProcessResponseDTOStatus.COMPLETED ||
      process.testSessionSummaries?.some(session => isComplete(session) || isRefused(session)),
    [talent.email, process.status, process.testSessionSummaries]
  );

  const getDisabledReason = useMemo(() => {
    if (process.status === ProcessResponseDTOStatus.UNPUBLISHED) {
      return t('processes:layouts.process.menu.disabledReasons.processUnpublished');
    }
    if (process.status === ProcessResponseDTOStatus.ON_HOLD) {
      return t('processes:layouts.process.menu.disabledReasons.processOnHold');
    }
    if (process.status === ProcessResponseDTOStatus.CANCELLED) {
      return t('processes:layouts.process.menu.disabledReasons.processCancelled');
    }
    if (process.status === ProcessResponseDTOStatus.COMPLETED) {
      return t('processes:layouts.process.menu.disabledReasons.processCompleted');
    }
    if (process.testSessionSummaries?.some(session => isComplete(session))) {
      return t('processes:layouts.process.menu.disabledReasons.talentCompleted');
    }
    if (process.testSessionSummaries?.some(session => isRefused(session))) {
      return t('processes:layouts.process.menu.disabledReasons.talentRefused');
    }

    return undefined;
  }, [process.status, process.testSessionSummaries, t]);

  const sendInvitationDisabledReason = useMemo(() => {
    if (!talent.email) {
      return t('processes:layouts.process.menu.disabledReasons.noEmail');
    }

    return getDisabledReason;
  }, [talent.email, getDisabledReason, t]);

  // If user doesn't have remove permission, don't show the menu at all
  const shouldShowMenu = canRemoveTalents;

  const handleRemoveTalentFromProcess = useCallback(() => {
    if (!process.id || !talent.id) return;

    removeTalentFromProcess(
      { id: process.id, data: { talentIds: [talent.id] } },
      {
        onSuccess: () => {
          setShowConfirmRemoveFromProcess(false);
          onRemoveSuccess?.();
        },
        onError: error => {
          if (error.response?.status === 403) {
            return showMessage(t('processes:layouts.process.menu.removeFromProcess.forbidden'));
          }

          return showMessage(t('common:error.somethingWentWrong'));
        }
      }
    );
  }, [process.id, talent.id, removeTalentFromProcess, onRemoveSuccess, showMessage, t]);

  const generatePassword = useCallback(async () => {
    if (!process.id || !talent.id) return;

    try {
      await inviteTalentToProcess({
        talentId: talent.id,
        processId: process.id,
        data: {
          accessMode: ProcessTalentInvitationRequestAccessMode.PASSWORD,
          sendMode: ProcessTalentInvitationRequestSendMode.MANUAL
        }
      });
    } catch {
      showMessage(t('common:error.somethingWentWrong'));
    }
  }, [inviteTalentToProcess, process.id, talent.id, showMessage, t]);

  const handleGeneratePassword = useCallback(async () => {
    await generatePassword();
    togglePasswordModalOpen();
  }, [generatePassword, togglePasswordModalOpen]);

  const [isSendingInvitation, setIsSendingInvitation] = useState(false);

  const handleSendInvitation = useCallback(async () => {
    if (!process.id || !talent.id) return;

    setIsSendingInvitation(true);
    try {
      await inviteTalentToProcess({
        talentId: talent.id,
        processId: process.id,
        data: {
          accessMode: ProcessTalentInvitationRequestAccessMode.UNIQUE_LINK,
          sendMode: ProcessTalentInvitationRequestSendMode.EMAIL
        }
      });
      showMessage(
        t('processes:layouts.process.sendInvitation.successMessage', {
          firstName: talent.firstName,
          lastName: talent.lastName
        })
      );
    } catch {
      showMessage(t('common:error.somethingWentWrong'));
    } finally {
      setIsSendingInvitation(false);
    }
  }, [inviteTalentToProcess, process.id, talent.id, talent.firstName, talent.lastName, showMessage, t]);

  if (!shouldShowMenu) {
    return null;
  }

  return (
    <>
      <DropdownMenu menuProps={menuProps} autoClose={autoClose}>
        <DropdownMenuItem
          text={t('processes:layouts.process.menu.removeFromProcess.menuLabel')}
          disabled={isRemoveDisabled}
          icon={<FolderMinusSign width={20} height={20} />}
          onClick={() => setShowConfirmRemoveFromProcess(true)}
        />
        {canGeneratePassword && (
          <DropdownMenuItem
            text={
              isGenerating
                ? t('processes:layouts.process.menu.generatePassword.generating')
                : t('processes:layouts.process.menu.generatePassword.menuLabel')
            }
            disabled={isGenerating || isGeneratePasswordDisabled}
            tooltip={isGeneratePasswordDisabled ? getDisabledReason : undefined}
            icon={<RectangleAsterisks width={20} height={20} />}
            onClick={handleGeneratePassword}
          />
        )}
        {canSendInvitationByEmail && (
          <DropdownMenuItem
            text={
              isSendingInvitation
                ? t('processes:layouts.process.menu.sendInvitation.sending')
                : t('processes:layouts.process.menu.sendInvitation.menuLabel')
            }
            disabled={isSendingInvitation || isSendInvitationDisabled}
            tooltip={isSendInvitationDisabled ? sendInvitationDisabledReason : undefined}
            icon={<PaperPlane width={20} height={20} />}
            onClick={handleSendInvitation}
          />
        )}
      </DropdownMenu>

      {/* Remove from process confirmation modal */}
      <ConfirmationModal
        open={showConfirmRemoveFromProcess}
        title={t('processes:layouts.process.menu.removeFromProcess.confirmation', {
          talentName: getUserFullName(talent),
          processName: process.name ?? ''
        })}
        confirmButtonLabel={t('processes:layouts.process.menu.removeFromProcess.confirm')}
        discardButtonLabel={t('processes:layouts.process.menu.removeFromProcess.cancel')}
        loadingLabel={t('processes:layouts.process.menu.removeFromProcess.removing')}
        illustration={<Box component="img" src="/images/folder-green-with-circle-check.svg" />}
        onConfirm={handleRemoveTalentFromProcess}
        onDiscard={() => setShowConfirmRemoveFromProcess(false)}
        onClose={() => setShowConfirmRemoveFromProcess(false)}
        isLoading={isRemoving}
      />

      <GeneratePasswordModal
        open={isPasswordModalOpen}
        onClose={togglePasswordModalOpen}
        invitationResponse={invitationResponse}
        onGenerateAgain={generatePassword}
        isGenerating={isGenerating}
      />
    </>
  );
};

export default TalentProcessActionsMenu;
